import { Coachmark, TeachingBubbleContent } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLink } from 'baseComponents';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapSmall } from 'globalStyles';
import Activity, { ActivityType } from 'models/activity';
import { EntityTypes } from 'models/entity';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCreateActivity, apiGetActivitiesForUserTypeEntity } from 'services/Api/activityService';
import { apiDismissActivity } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import {
  CoachMarkDefinition,
  CoachTypes,
  getCoachMarkFormsMoved,
  getCoachMarkInGoodCompany26,
  getCoachMarkInheritance,
  getCoachMarkMainMenuChanged,
  getCoachMarkTagsMoved,
  getCoachMarkTaskFilterLocation,
  getCoachMarkTimeline,
} from './CoachMarkDefinitions';
import { IPTarget } from "baseComponents/Utils/Target";

interface ICoachProps {
  target: IPTarget;
  type: CoachTypes;
  direction?: IPDirectionalHint;
}

const Coach = (props: ICoachProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'coachmarks']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [show, setShow] = useState<boolean>(false);
  const [activity, setActivity] = useState<Activity | undefined>(undefined);

  // Both the lookup and the dismissal below hit tenant-scoped activity endpoints. During the /start trial
  // bootstrap the user is already authenticated while the tenant is not registered yet, and every such call
  // is refused with a 401 that would land in the app-wide error banner. Wait for a licensed user, which is
  // set on the profile reload right after the trial has been created, so the coachmark still shows once.
  const isReady = appContext.isAuthenticated && appContext.user.login.userLicensed;

  useEffect(() => {
    if (!isReady) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const activities = await apiGetActivitiesForUserTypeEntity(
        ActivityType.UserNotification,
        EntityTypes.CoachMark,
        props.type,
        accessToken,
      );
      const activity = activities[0];
      setActivity(activity);
      const shouldShow = activity?.dismissed === undefined;
      setShow(shouldShow);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const saveData = async (dismiss: boolean) => {
    try {
      setShow(false);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      if (activity) {
        if (dismiss) {
          await apiDismissActivity(activity.activityId, accessToken);
        }
      } else {
        const newActivity = new Activity();
        newActivity.typeOfActivity = ActivityType.UserNotification;
        newActivity.entity.typeOfEntity = EntityTypes.CoachMark;
        newActivity.entity.entityId = props.type;
        newActivity.dismissed = dismiss ? new Date() : undefined;
        newActivity.userId = appContext.user?.id;
        const createdActivity = await apiCreateActivity(newActivity, true, true, true, accessToken);
        if (createdActivity) {
          setActivity(createdActivity);
        } else {
          setActivity(newActivity); //there is an activity in the back-end, we could retrieve it but this will do: we don't need the Id
        }
      }
    } catch (err) {
      appContext.setError(err);
    }
  };

  //
  // Main render
  //
  if (!isReady || isLoading || !show) {
    return null;
  }

  let content: CoachMarkDefinition | undefined;

  switch (props.type) {
    case CoachTypes.Timeline:
      content = getCoachMarkTimeline(t);
      break;
    case CoachTypes.Inheritance:
      content = getCoachMarkInheritance(t);
      break;
    case CoachTypes.TaskFilterLocation:
      content = getCoachMarkTaskFilterLocation(t);
      break;
    case CoachTypes.FormsMoved:
      content = getCoachMarkFormsMoved(t);
      break;
    case CoachTypes.TagsMoved:
      content = getCoachMarkTagsMoved(t);
      break;
    case CoachTypes.InGoodCompany26:
      content = getCoachMarkInGoodCompany26(t);
      break;
    case CoachTypes.MainMenuChanged:
      content = getCoachMarkMainMenuChanged(t);
      break;
    default:
      return null;
  }

  const ctaText = content.ctaText;
  const ctaUrl = content.ctaUrl;

  return (
    <Coachmark
      target={props.target}
      delayBeforeMouseOpen={1000}
      positioningContainerProps={{
        directionalHint: props.direction,
      }}
    >
      <TeachingBubbleContent
        headline={content.title}
        hasCloseButton
        onDismiss={() => {
          saveData(false); // close only: the coachmark is shown again on the next visit
        }}
        styles={{
          // A long coachmark can run past the bottom of the window, taking the footer buttons with it
          // and leaving the user no way to close it. Cap the text and let it scroll instead, so the
          // headline, the close button and the buttons stay inside the viewport on any screen height.
          body: { maxHeight: '35vh', overflowY: 'auto' },
        }}
        primaryButtonProps={{
          text: t('coachmarks:Buttons.GotIt'),
          onClick: () => {
            saveData(true);
          },
        }}
        secondaryButtonProps={{
          text: t('coachmarks:Buttons.RemindMeLater'),
          onClick: () => {
            saveData(false);
          },
        }}
      >
        <IPStack tokens={globalStackTokensGapSmall}>
          <IPStack.Item>{content.jsx}</IPStack.Item>
          {ctaUrl && ctaText && (
            <IPStack.Item>
              <IPLink
                underline
                styles={{ root: { color: 'inherit' } }}
                onClick={() => {
                  window.open(ctaUrl, '_blank', 'noreferrer');
                  saveData(true);
                }}
              >
                {ctaText}
              </IPLink>
            </IPStack.Item>
          )}
        </IPStack>
      </TeachingBubbleContent>
    </Coachmark>
  );
};

export default Coach;
